import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { programs } from 'layers/content/v2/hooks/useCourseManager'
import { CourseManagerType } from 'layers/content/v2/Hocs/CourseManagerGetter'
import { CurriculumCourseManagerGetter } from 'layers/content/v3/Hocs/CurriculumCourseManagerGetter'
import {
  operations as lmsOperations,
  selectors as lmsSelectors,
} from 'store/lmsManager'
import { selectors as contextSelectors } from 'auth/stores/userContext'
import { selectors as licenseSelectors } from 'store/licenseManager'
import {
  AUDIENCE_SCHOOL_LEADER,
  AUDIENCE_EDUCATOR,
  findNode,
} from 'utils/highschoolHelpers'
import HsEducatorCompleteModal from 'components/HsEducatorCompleteModal'
import Component from 'components/HsIndividualResourceMarkComplete/component'
import {
  formatDate,
  formatSchoolName,
  getMarkCompleteButtonIcons,
  getCompletionCommentary,
  getMarkCompleteButtonText,
  getEducatorCompleteModalContent,
  getEducatorProgressText,
} from 'components/HsIndividualResourceMarkComplete/helpers'
import { PERIOD_PROGRESS } from 'components/HsIndividualResourceMarkComplete/constants'
import {
  doesRefreshMatchAttempt,
  getHsLessonStatus,
  isAdminAtSite,
} from './helpers'

const LOG_PREFIX = '[hs-mark-complete]'

export function HsIndividualResourceMarkCompleteV2Container({
  activeSites,
  activityType,
  courseManager,
  courseNodeId,
  highSchoolInstructorData,
  isCreatingProgramFlex,
  isFetchingOrCreatingProgramFlex,
  markCompleteContent,
  markHsLessonComplete,
  practiceName,
  siteId: siteIdProp,
  userContext,
}) {
  const [educatorProgressModalOpen, setEducatorProgressModalOpen] = useState(
    false,
  )
  const [isSubmitting, setIsSubmitting] = useState(false)

  const siteId = siteIdProp ?? userContext?.hsActiveSiteId
  const courseTree = courseManager?.courseTrees?.highschool
  const leafNodeId = useMemo(
    () =>
      (courseTree && findNode(courseTree, courseNodeId)?.leafNodeId) || null,
    [courseTree, courseNodeId],
  )
  const schoolName = useMemo(() => {
    if (siteId == null) return ''
    const site = activeSites?.find(({ id }) => String(id) === String(siteId))
    return formatSchoolName(site?.name) || ''
  }, [activeSites, siteId])
  const { registration, isComplete, date, periodProgress } = useMemo(
    () =>
      getHsLessonStatus({
        instructorData: highSchoolInstructorData,
        siteId,
        activityType,
        courseNodeId,
      }),
    [highSchoolInstructorData, siteId, activityType, courseNodeId],
  )

  const isEducatorPractice = activityType === AUDIENCE_EDUCATOR
  const isAdminUser = isAdminAtSite({ userContext, siteId })
  // Schoolwide Activities can only be completed by admins
  const isDisabled = activityType === AUDIENCE_SCHOOL_LEADER && !isAdminUser
  const isInstructorDataLoading =
    !!isCreatingProgramFlex || !!isFetchingOrCreatingProgramFlex
  const isReady =
    !!registration?.registration_id && !!leafNodeId && !isInstructorDataLoading

  const {
    icon: markCompleteButtonIconUrl,
    hoverIcon: markCompleteButtonIconUrlHover,
  } = getMarkCompleteButtonIcons({
    isComplete,
    isDisabled,
    isEducatorPractice,
    educatorPracticeStatus: periodProgress,
  })

  function shouldShowCelebrationIcon() {
    if (!isComplete) return false
    // NOTE: if periodProgress is somehow unset, just always show the icon
    if (isEducatorPractice && periodProgress) {
      return (
        periodProgress === PERIOD_PROGRESS.ALL ||
        periodProgress === PERIOD_PROGRESS.MOST
      )
    }
    return true
  }

  async function submitCompletion(completion) {
    if (isSubmitting || !isReady) return

    const registrationId = registration?.registration_id
    if (!registrationId || !leafNodeId) {
      console.error(`${LOG_PREFIX} missing ids`, {
        siteId,
        activityType,
        courseNodeId,
        registrationId,
        leafNodeId,
      })
      return
    }

    setIsSubmitting(true)
    try {
      const instructorData = await markHsLessonComplete({
        registrationId,
        leafNodeId,
        ...completion,
      })

      if (!instructorData) {
        console.warn(`${LOG_PREFIX} refresh returned no data`, {
          registrationId,
          leafNodeId,
        })
        return
      }

      const lessonStatus = getHsLessonStatus({
        instructorData: instructorData.HighSchool,
        siteId,
        activityType,
        courseNodeId,
      })
      const matches = doesRefreshMatchAttempt({
        lessonStatus,
        periodProgress: completion.periodProgress,
      })
      if (!matches) {
        console.warn(`${LOG_PREFIX} refresh does not reflect the attempt`, {
          registrationId,
          leafNodeId,
          lessonStatus,
        })
      }
    } catch (error) {
      console.error(`${LOG_PREFIX} attempt failed`, {
        registrationId,
        leafNodeId,
        error,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  function handleMarkCompleteButtonClicked() {
    if (isDisabled || isSubmitting || !isReady) return

    if (isEducatorPractice) {
      // the modal collects the period progress and sends the attempt on save
      setEducatorProgressModalOpen(true)
      return
    }
    if (isComplete) return

    submitCompletion({ progress: 1 })
  }

  function handleEducatorProgressModalSubmit(progress) {
    setEducatorProgressModalOpen(false)
    // saving the value LMS already has would be a redundant request
    if (!progress || progress === periodProgress) return

    submitCompletion({ periodProgress: progress })
  }

  useEffect(() => {
    if (courseTree && !leafNodeId) {
      console.warn(`${LOG_PREFIX} courseNodeId not found in course tree`, {
        courseNodeId,
      })
    }
  }, [courseTree, courseNodeId, leafNodeId])

  useEffect(() => {
    if (isInstructorDataLoading || !highSchoolInstructorData) return
    if (!registration?.registration_id) {
      console.warn(`${LOG_PREFIX} no registration for this site`, {
        siteId,
        activityType,
      })
    }
  }, [
    activityType,
    highSchoolInstructorData,
    isInstructorDataLoading,
    registration,
    siteId,
  ])

  return (
    <>
      <Component
        canReupdateProgress={isEducatorPractice}
        completionCommentary={getCompletionCommentary({
          markCompleteContent,
          schoolName,
          completionDate: formatDate(date) || '',
          isComplete,
          activityType,
        })}
        completionStatusDetail={
          isEducatorPractice
            ? getEducatorProgressText({ markCompleteContent, periodProgress })
                ?.progressCompletionText || ''
            : ''
        }
        handleMarkComplete={handleMarkCompleteButtonClicked}
        hasSurvey={
          isComplete && (markCompleteContent?.surveyButtonText || false)
        }
        isComplete={isComplete}
        isDisabled={isDisabled}
        isSubmitting={isSubmitting || !isReady}
        markCompleteButtonIconUrl={markCompleteButtonIconUrl}
        markCompleteButtonIconUrlHover={markCompleteButtonIconUrlHover}
        markCompleteButtonText={getMarkCompleteButtonText({
          markCompleteContent,
          isComplete,
          isAdmin: isAdminUser,
          periodProgress,
        })}
        shouldShowCelebrationIcon={shouldShowCelebrationIcon()}
        surveyButtonText={markCompleteContent?.surveyButtonText}
        surveyCommentary={markCompleteContent?.surveyText}
      />
      {isEducatorPractice && (
        <HsEducatorCompleteModal
          {...getEducatorCompleteModalContent(markCompleteContent)}
          handleClose={() => setEducatorProgressModalOpen(false)}
          initialValue={periodProgress}
          practiceName={practiceName}
          show={educatorProgressModalOpen}
          submitProgress={handleEducatorProgressModalSubmit}
        />
      )}
    </>
  )
}

HsIndividualResourceMarkCompleteV2Container.propTypes = {
  activeSites: PropTypes.array,
  activityType: PropTypes.string,
  courseManager: CourseManagerType,
  courseNodeId: PropTypes.string,
  highSchoolInstructorData: PropTypes.object,
  isCreatingProgramFlex: PropTypes.bool,
  isFetchingOrCreatingProgramFlex: PropTypes.bool,
  markCompleteContent: PropTypes.object,
  markHsLessonComplete: PropTypes.func,
  practiceName: PropTypes.string,
  siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userContext: PropTypes.object,
}

const mapStateToProps = state => ({
  activeSites: licenseSelectors.selectActiveSites(state),
  highSchoolInstructorData: lmsSelectors.selectHsInstructorData(state),
  isCreatingProgramFlex: state.lmsManager?.isCreatingProgramFlex,
  isFetchingOrCreatingProgramFlex:
    state.lmsManager?.isFetchingOrCreatingProgramFlex,
  userContext: contextSelectors.selectProgramContext(state),
})

const mapDispatchToProps = {
  markHsLessonComplete: lmsOperations.markHsLessonComplete,
}

// this component is only ever used in High School
const programResolver = () => programs.highSchool

export const authoredCourseManagerOptions = {
  programResolver,
  allowAuthoredCourseTree: true,
}

const CourseManagerWrapped = CurriculumCourseManagerGetter(
  HsIndividualResourceMarkCompleteV2Container,
  authoredCourseManagerOptions,
)

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(CourseManagerWrapped)
